import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { APP_CONFIG, ROUTER_CONFIG } from '@core/configs';
import { CustomHttpParamEncoder } from '@core/helper/custom-param-encoder.helper';
import { LocalStorageHelper, LocalStorageKey } from '@core/helper/local-storage.helper';
import { ApiResponse } from '@core/models/interfaces';
import { NEVER, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private readonly http: HttpClient, private readonly router: Router) {}

  /**
   * GET request
   * Ex: [param1, param2, param3]
   * => apiUrl/param1/param2/param3
   */
  get<T = any>(apiUrl: string, parameter: any[] = []): Observable<ApiResponse<T>> {
    parameter.forEach(p => {
      apiUrl += '/' + p;
    });

    return this.http
      .get<T>(apiUrl, {
        headers: this.getHeaders(),
        observe: 'response',
      })
      .pipe(
        map(this.forwardData),
        catchError(e => this.forwardError(e))
      );
  }

  /**
   * GET request with query
   * Ex: { param1: value1, param2: value2, param3: value3 }
   * => apiUrl?param1=value1&param2=value2&param3=value3
   */
  getWithQuery<T = any>(
    apiUrl: string,
    parameter: { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> }
  ): Observable<ApiResponse<T>> {
    return this.http
      .get<T>(apiUrl, {
        headers: this.getHeaders(),
        params: this.getParams(parameter),
        observe: 'response',
      })
      .pipe(
        map(this.forwardData),
        catchError(e => this.forwardError(e))
      );
  }

  /**
   * POST request
   */
  post<T = any>(apiUrl: string, body?: any): Observable<ApiResponse<T>> {
    return this.http
      .post<T>(apiUrl, body || {}, {
        headers: this.getHeaders(),
        observe: 'response',
      })
      .pipe(
        map(this.forwardData),
        catchError(e => this.forwardError(e))
      );
  }

  /**
   * POST request with query
   */
  postWithQuery<T = any>(
    apiUrl: string,
    parameter: { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> },
    body?: any
  ): Observable<ApiResponse<T>> {
    return this.http
      .post<T>(apiUrl, body || {}, {
        headers: this.getHeaders(),
        params: this.getParams(parameter),
        observe: 'response',
      })
      .pipe(
        map(this.forwardData),
        catchError(e => this.forwardError(e))
      );
  }

  /**
   * PUT request
   */
  put<T = any>(apiUrl: string, body?: any): Observable<ApiResponse<T>> {
    return this.http
      .put<T>(apiUrl, body || {}, {
        headers: this.getHeaders(),
        observe: 'response',
      })
      .pipe(
        map(this.forwardData),
        catchError(e => this.forwardError(e))
      );
  }

  /**
   * DELETE request
   */
  delete<T = any>(apiUrl: string, data?: any | any[]): Observable<ApiResponse<T>> {
    if (data) {
      if (Array.isArray(data)) {
        data.forEach((p: string) => {
          apiUrl += '/' + p;
        });
      } else if (typeof data === 'object') {
        return this.http
          .request<T>('delete', apiUrl, {
            body: data || {},
            headers: this.getHeaders(),
            observe: 'response',
          })
          .pipe(
            map(this.forwardData),
            catchError(e => this.forwardError(e))
          );
      } else {
        apiUrl += '/' + data;
      }
    }
    return this.http
      .delete<T>(apiUrl, {
        headers: this.getHeaders(),
        observe: 'response',
      })
      .pipe(
        map(this.forwardData),
        catchError(e => this.forwardError(e))
      );
  }

  /**
   * DELETE request with query
   */
  deleteWithQuery<T = any>(
    apiUrl: string,
    parameter: { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> },
    data?: any | any[]
  ): Observable<ApiResponse<T>> {
    if (data) {
      if (Array.isArray(data)) {
        data.forEach((p: string) => {
          apiUrl += '/' + p;
        });
      } else if (typeof data === 'object') {
        return this.http
          .request<T>('delete', apiUrl, {
            body: data || {},
            headers: this.getHeaders(),
            params: this.getParams(parameter),
            observe: 'response',
          })
          .pipe(
            map(this.forwardData),
            catchError(e => this.forwardError(e))
          );
      } else {
        apiUrl += '/' + data;
      }
    }
    return this.http
      .delete<T>(apiUrl, {
        headers: this.getHeaders(),
        params: this.getParams(parameter),
        observe: 'response',
      })
      .pipe(
        map(this.forwardData),
        catchError(e => this.forwardError(e))
      );
  }

  /**
   * Forward data
   */
  private forwardData<T = any>(res: HttpResponse<T>): ApiResponse<T> {
    return { result: res.body as any, status: res.status, message: 'successful' };
  }

  /**
   * Request Error handler
   */
  private forwardError(error: HttpErrorResponse): Observable<never> {
    if (Number(error.status) === HttpStatusCode.Forbidden) {
      this.router.navigate([ROUTER_CONFIG.forbidden], { skipLocationChange: true });
      LocalStorageHelper.setItem(LocalStorageKey.NO_PERMISSION, true);
      return NEVER;
    } else if (Number(error.status) === HttpStatusCode.NotFound) {
      this.router.navigate([ROUTER_CONFIG.notFound], { skipLocationChange: true });
      return throwError(error);
    } else {
      return throwError(error);
    }
  }

  /**
   * Get HttpParams from object
   */
  private getParams(obj: { [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean> }): HttpParams {
    return new HttpParams({ encoder: new CustomHttpParamEncoder(), fromObject: obj });
  }

  /**
   * Common header for each API
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'content-type': 'application/json',
      // eslint-disable-next-line quote-props
      module: APP_CONFIG.module,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'x-requested-with': 'XMLHttpRequest',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'access-control-allow-origin': '*',
    });
  }
}
